import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, MessageCircle, Phone, Truck, Upload } from "lucide-react";
import { EASE, MARK, Reveal, MaskedHeading, gbp } from "@/components/common";
import { WHATSAPP_DISPLAY, waLink } from "@/data/vehicles";
import { apiFetch } from "@/api";

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="field-label">{label}</span>
    <input className="field" data-testid={testid} {...props} />
  </label>
);

const ChoiceRow = ({ label, options, value, onPick, testid }) => (
  <div data-testid={testid}>
    <span className="field-label">{label}</span>
    <div className={`grid gap-3 ${options.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
      {options.map((o) => (
        <button
          key={o}
          data-testid={`${testid}-${o.toLowerCase().replace(/\s/g, "-")}`}
          onClick={() => onPick(o)}
          className={`radio-row justify-center !py-3.5 !px-2 ${value === o ? "selected" : ""}`}
        >
          <span className="text-[0.6rem] tracking-[0.22em] uppercase">{o}</span>
        </button>
      ))}
    </div>
  </div>
);

const QuotePanel = ({ q }) => {
  const local = q.pricing_type === "local";
  return (
    <div data-testid="recovery-quote" className="shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)] bg-white/[0.02] p-6 md:p-8">
      <div className="flex items-center justify-between gap-6 py-2.5 border-b border-white/[0.06]">
        <span className="text-sm text-zinc-400 font-light">Pricing</span>
        <span className="text-sm text-zinc-100 font-light text-right" data-testid="recovery-pricing-type">
          {local ? "Local Peterborough Recovery — Fixed Rate" : "Out-of-Town Recovery — Mileage Rate"}
        </span>
      </div>
      {!local && [
        ["Truck travel — PE4 6BX to breakdown", `${q.truck_miles} miles`],
        ["Recovery journey — breakdown to destination", `${q.recovery_miles} miles`],
        ["Total billable mileage", `${q.total_miles} miles`],
        ["Rate band", q.band_label],
        ["Mileage rate", `£${q.rate.toFixed(2)} per mile`],
      ].map(([l, v]) => (
        <div key={l} className="flex items-center justify-between gap-6 py-2.5 border-b border-white/[0.06]">
          <span className="text-sm text-zinc-400 font-light">{l}</span>
          <span className="text-sm text-zinc-100 font-light text-right">{v}</span>
        </div>
      ))}
      <div className="flex items-center justify-between pt-4 mt-1">
        <span className="text-[0.65rem] tracking-[0.3em] uppercase text-white">
          {local ? "Local Recovery — Fixed Price" : "Estimated Recovery Total"}
        </span>
        <span className="font-display text-2xl text-white" data-testid="recovery-estimate">
          {local && !q.estimated_total ? "Quote Pending" : gbp(q.estimated_total)}
        </span>
      </div>
      <p className="mt-4 text-[0.62rem] tracking-[0.12em] text-zinc-500 font-light">
        {local
          ? q.estimated_total
            ? "Fixed local rate — your recovery is confirmed once our team accepts the request."
            : "Local fixed-rate recovery — our team will confirm your fixed quote before any payment is taken."
          : "Estimate only — your recovery is confirmed once our team accepts the request."}
      </p>
    </div>
  );
};

export default function Recovery() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    breakdown_postcode: "", destination_postcode: "",
    vehicle_make: "", vehicle_model: "", vehicle_reg: "",
    description: "", recovery_date: "", recovery_time: "", condition: "",
    passengers_safe: "", vehicle_safe: "", neutral: "", turns_over: "", smoking: "",
  });
  const [photos, setPhotos] = useState([]);
  const [quote, setQuote] = useState(null);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState("");

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setError(""); };

  const calcQuote = async () => {
    if (!form.breakdown_postcode.trim() || !form.destination_postcode.trim() || !form.recovery_time) {
      setError("Add the breakdown postcode, destination postcode and recovery time to calculate the estimate.");
      return;
    }
    setQuoteBusy(true);
    setError("");
    try {
      setQuote(await apiFetch("/recovery-quote", {
        method: "POST",
        body: {
          breakdown_postcode: form.breakdown_postcode,
          destination_postcode: form.destination_postcode,
          recovery_time: form.recovery_time,
        },
      }));
    } catch (e) {
      setQuote(null);
      setError(e.message);
    } finally {
      setQuoteBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await apiFetch("/recovery-requests", { method: "POST", body: form });
      for (const file of photos) {
        const fd = new FormData();
        fd.append("file", file);
        await apiFetch(`/recovery-requests/${res.ref}/photos`, { method: "POST", formData: fd });
      }
      setSent(res);
      window.scrollTo(0, 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#050506] text-zinc-100 min-h-screen">
      <div className="vignette-overlay" />
      <header className="border-b border-white/[0.08] relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="recovery-home-link" className="flex items-center gap-3">
            <img src={MARK} alt="Senna Prestige Travels" className="h-8 w-auto" />
            <span className="hidden sm:block text-[0.6rem] tracking-[0.42em] uppercase text-zinc-300">Senna Recovery</span>
          </Link>
          <Link to="/" data-testid="recovery-back-link" className="nav-link text-[0.62rem] tracking-[0.3em] uppercase text-zinc-400">Back to Site</Link>
        </div>
      </header>

      <main className="relative z-10">
        <section className="max-w-5xl mx-auto px-6 md:px-10 pt-24 md:pt-36 pb-20 text-center">
          <Reveal>
            <p className="kicker mb-6">Senna Prestige Travels</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.1]">
              <MaskedHeading>Senna Recovery</MaskedHeading>
            </h1>
            <p className="mt-8 text-sm md:text-base text-zinc-400 font-light leading-relaxed max-w-xl mx-auto">
              Professional vehicle recovery operating two recovery trucks, based in Peterborough.
            </p>
          </Reveal>

          <Reveal delay={0.15} className="mt-14">
            <div className="inline-block shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] bg-white/[0.02] px-8 md:px-16 py-10">
              <p className="text-[0.6rem] tracking-[0.4em] uppercase text-zinc-500">Out-of-Town Recovery Rates</p>
              <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12">
                <div>
                  <p className="font-display text-2xl sm:text-3xl text-white">£2.50<span className="text-base text-zinc-400"> / mile</span></p>
                  <p className="mt-2 text-[0.58rem] tracking-[0.25em] uppercase text-zinc-500">Day Rate · 06:00–21:00</p>
                </div>
                <div className="hidden sm:block w-px h-12 bg-white/10" />
                <div>
                  <p className="font-display text-2xl sm:text-3xl text-white">£3.00<span className="text-base text-zinc-400"> / mile</span></p>
                  <p className="mt-2 text-[0.58rem] tracking-[0.25em] uppercase text-zinc-500">Night Rate · 21:00–06:00</p>
                </div>
              </div>
              <p className="mt-6 text-[0.62rem] tracking-[0.14em] text-zinc-500 font-light">
                Local Peterborough recoveries are charged at a fixed local rate — no per-mile pricing.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.25} className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-5">
            <a data-testid="recovery-request-btn" href="#request" className="btn-primary justify-center">
              <Truck size={14} /> Request Recovery
            </a>
            <a data-testid="recovery-call-btn" href="tel:+447457418474" className="btn-ghost justify-center">
              <Phone size={14} /> Call {WHATSAPP_DISPLAY}
            </a>
            <a data-testid="recovery-whatsapp-btn" href={waLink("Hello Senna Recovery — I need recovery assistance.")} target="_blank" rel="noopener noreferrer" className="btn-ghost justify-center">
              <MessageCircle size={14} /> WhatsApp Us
            </a>
          </Reveal>
        </section>

        <section id="request" className="border-t border-white/[0.07] bg-[#08080a] scroll-mt-10">
          <div className="max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-32">
            {sent ? (
              <motion.div data-testid="recovery-sent" className="text-center" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full shadow-[inset_0_0_0_1px_rgba(255,255,255,0.25)] mb-6">
                  <Check size={22} className="text-white" strokeWidth={1.5} />
                </span>
                <p className="kicker mb-4">Reference {sent.ref}</p>
                <h2 className="font-display text-3xl sm:text-4xl text-white">Recovery Request Received</h2>
                <p className="mt-6 text-sm md:text-base text-zinc-400 font-light leading-relaxed max-w-lg mx-auto">
                  Thank you — your request is with our team. Senna Prestige Travels will confirm truck and driver availability before your recovery is booked in. If available, you will receive a secure payment link — a 30% deposit is payable before the driver sets off, with the remaining 70% authorised on your card and only captured on completion. For anything urgent, call or WhatsApp {WHATSAPP_DISPLAY}.
                </p>
                <div className="mt-10 text-left"><QuotePanel q={sent} /></div>
              </motion.div>
            ) : (
              <Reveal>
                <p className="kicker mb-5 text-center">Request Recovery</p>
                <h2 className="font-display text-3xl sm:text-4xl text-white text-center leading-[1.15]">
                  <MaskedHeading>Tell us where you are.</MaskedHeading>
                </h2>
                <div className="mt-12 space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Your Name" testid="recovery-name" placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                    <Field label="Mobile Number" testid="recovery-phone" placeholder="Best contact number" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                  </div>
                  <Field label="Email (optional)" testid="recovery-email" type="email" placeholder="you@example.com" value={form.email} onChange={(e) => set("email", e.target.value)} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Breakdown Postcode / Location" testid="recovery-breakdown" placeholder="Where is the vehicle?" value={form.breakdown_postcode} onChange={(e) => set("breakdown_postcode", e.target.value)} />
                    <Field label="Destination Postcode / Location" testid="recovery-destination" placeholder="Where should it go?" value={form.destination_postcode} onChange={(e) => set("destination_postcode", e.target.value)} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                    <Field label="Vehicle Make" testid="recovery-make" placeholder="e.g. BMW" value={form.vehicle_make} onChange={(e) => set("vehicle_make", e.target.value)} />
                    <Field label="Vehicle Model" testid="recovery-model" placeholder="e.g. 320d" value={form.vehicle_model} onChange={(e) => set("vehicle_model", e.target.value)} />
                    <Field label="Registration" testid="recovery-reg" placeholder="e.g. AB12 CDE" value={form.vehicle_reg} onChange={(e) => set("vehicle_reg", e.target.value)} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Requested Recovery Date" testid="recovery-date" type="date" value={form.recovery_date} onChange={(e) => set("recovery_date", e.target.value)} />
                    <Field label="Requested Recovery Time" testid="recovery-time" type="time" value={form.recovery_time} onChange={(e) => set("recovery_time", e.target.value)} />
                  </div>
                  <div>
                    <span className="field-label">Vehicle Condition</span>
                    <div className="grid grid-cols-2 gap-4">
                      {["Drivable", "Non-drivable"].map((c) => (
                        <button
                          key={c}
                          data-testid={`recovery-condition-${c.toLowerCase()}`}
                          onClick={() => set("condition", c)}
                          className={`radio-row justify-center ${form.condition === c ? "selected" : ""}`}
                        >
                          <span className="text-[0.62rem] tracking-[0.3em] uppercase">{c}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="pt-7 border-t border-white/[0.08]">
                    <p className="text-[0.68rem] tracking-[0.3em] uppercase text-white mb-5">Incident, Safety & Vehicle Condition</p>
                    <label className="block">
                      <span className="field-label">What has happened?</span>
                      <textarea data-testid="recovery-description" className="field min-h-[90px] resize-y" placeholder="Brief description — e.g. engine won't start, accident, flat tyre…" value={form.description} onChange={(e) => set("description", e.target.value)} />
                    </label>
                    <div className="mt-6 space-y-6">
                      <ChoiceRow label="Are you and any passengers currently safe?" options={["Yes", "No"]} value={form.passengers_safe} onPick={(v) => set("passengers_safe", v)} testid="recovery-passengers-safe" />
                      <ChoiceRow label="Is the vehicle currently in a safe position?" options={["Yes", "No", "Not sure"]} value={form.vehicle_safe} onPick={(v) => set("vehicle_safe", v)} testid="recovery-vehicle-safe" />
                      <ChoiceRow label="Does the vehicle go into neutral?" options={["Yes", "No", "Not sure"]} value={form.neutral} onPick={(v) => set("neutral", v)} testid="recovery-neutral" />
                      <ChoiceRow label="Is the vehicle turned over?" options={["Yes", "No", "Not sure"]} value={form.turns_over} onPick={(v) => set("turns_over", v)} testid="recovery-turns-over" />
                      <ChoiceRow label="Is the vehicle smoking?" options={["Yes", "No", "Not sure"]} value={form.smoking} onPick={(v) => set("smoking", v)} testid="recovery-smoking" />
                    </div>
                    <div className="mt-6">
                      <span className="field-label">Photos (optional)</span>
                      <div className="file-drop" data-testid="recovery-photos">
                        <input type="file" id="recovery-photos-input" className="hidden" accept="image/*" multiple onChange={(e) => setPhotos((p) => [...p, ...Array.from(e.target.files || [])])} />
                        <label htmlFor="recovery-photos-input" className="flex items-center justify-between gap-4 cursor-pointer px-5 py-4">
                          <span className="text-sm font-light text-zinc-400 flex items-center gap-3">
                            <Upload size={14} className="text-zinc-500" />
                            {photos.length ? `${photos.length} photo${photos.length > 1 ? "s" : ""} selected` : "Take or add photos of the vehicle and area"}
                          </span>
                          <span className="text-[0.55rem] tracking-[0.25em] uppercase text-zinc-500">Add</span>
                        </label>
                        {photos.length > 0 && (
                          <div className="px-5 pb-4 flex flex-wrap gap-3">
                            {photos.map((f, i) => (
                              <span key={i} className="text-[0.6rem] tracking-[0.12em] text-zinc-400 flex items-center gap-2">
                                {f.name}
                                <button data-testid={`recovery-photo-remove-${i}`} onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))} className="text-zinc-600 hover:text-red-300 text-sm leading-none">×</button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="mt-3 text-[0.62rem] tracking-[0.12em] text-zinc-600 font-light">Photos are uploaded securely and are only visible to our team — never public, never emailed.</p>
                    </div>
                  </div>

                  <button data-testid="recovery-calc" onClick={calcQuote} disabled={quoteBusy} className="btn-ghost w-full justify-center disabled:opacity-50">
                    {quoteBusy ? "Calculating…" : "Calculate Estimate"}
                  </button>

                  {quote && <QuotePanel q={quote} />}

                  {error && <p data-testid="recovery-error" className="text-xs text-amber-200/90 font-light">{error}</p>}
                  <button data-testid="recovery-submit" onClick={submit} disabled={busy} className="btn-primary w-full justify-center disabled:opacity-50">
                    <Truck size={14} /> {busy ? "Sending…" : "Submit Recovery Request"}
                  </button>
                  <p className="text-[0.62rem] tracking-[0.12em] text-zinc-600 font-light text-center">
                    Submitting a request does not book the truck — our team confirms availability first.
                  </p>
                </div>
              </Reveal>
            )}
          </div>
        </section>

        <footer className="border-t border-white/[0.07]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-[0.58rem] tracking-[0.35em] uppercase text-zinc-600">Senna Recovery — by Senna Prestige Travels</span>
            <a href="tel:+447457418474" data-testid="recovery-footer-phone" className="text-[0.58rem] tracking-[0.3em] uppercase text-zinc-500 hover:text-white transition-colors">{WHATSAPP_DISPLAY}</a>
          </div>
        </footer>
      </main>
    </div>
  );
}
